import { Injectable } from '@angular/core';
import { Action, Actions, createSelector, ofActionDispatched, ofActionSuccessful, Selector, State, StateContext, Store } from '@ngxs/store';
import _ from 'lodash';
import { distinctUntilChanged, filter, map } from 'rxjs/operators';
import { TerminateAllUserSessions, TerminateCurrentSession } from '../../session/session.actions';
import {
   NativeNotificationAddRequestToClose,
   NativeNotificationAddRequestToOpen,
   NativeNotificationAddTemplate,
   NativeNotificationCloseByKey,
   NativeNotificationFinishClose,
   NativeNotificationRemoveRequestToClose,
   NativeNotificationRemoveRequestToOpen,
   NativeNotificationRemoveTemplate,
   NativeNotificationSetHighestFiredNativeKeys,
   NativeNotificationSetOpenNotifications,
} from './notification-native.actions';

export interface NotificationKey {
   tag: string;
   id: number;
}

export interface NotificationContent {
   title: any;
   content: any;
}

export interface NotificationTemplate {
   key: NotificationKey;
   notificationContent: NotificationContent;
}

export interface NativeNotification {
   key: NotificationKey;
   notification: Notification;
}

export interface NotificationNativeStateModel {
   highestFiredKeys: NotificationKey[];
   notificationTemplate: NotificationTemplate[];
   openNotifications: NativeNotification[];
   closedNotifications: NativeNotification[];
   requestedToOpen: NotificationKey[];
   requestedToClose: NotificationKey[];
}

@State<NotificationNativeStateModel>({
   name: 'notificationNative',
   defaults: {
      highestFiredKeys: [],
      notificationTemplate: [],
      requestedToOpen: [],
      requestedToClose: [],
      openNotifications: [],
      closedNotifications: [],
   },
})
@Injectable()
export class NotificationNativeState {
   constructor(private actions$: Actions, private store: Store) {
      actions$
         .pipe(
            ofActionDispatched(TerminateCurrentSession),
            map(() => store.selectSnapshot(NotificationNativeState.openNotificationKeys))
         )
         .subscribe((keys) => store.dispatch(new NativeNotificationCloseByKey(keys)));
      actions$
         .pipe(
            ofActionSuccessful(TerminateAllUserSessions),
            map(() => store.selectSnapshot(NotificationNativeState.openNotificationKeys))
         )
         .subscribe((keys) => store.dispatch(new NativeNotificationCloseByKey(keys)));

      this.store
         .select(NotificationNativeState.oldTemplateKeys)
         .pipe(
            distinctUntilChanged(),
            filter((keys) => !_.isEmpty(keys))
         )
         .subscribe((keys) => {
            this.store.dispatch(new NativeNotificationRemoveTemplate(keys));
         });

      this.store
         .select(NotificationNativeState.oldrequestToOpenKeys)
         .pipe(
            distinctUntilChanged(),
            filter((keys) => !_.isEmpty(keys))
         )
         .subscribe((keys) => {
            this.store.dispatch(new NativeNotificationRemoveRequestToOpen(keys));
         });

      this.store
         .select(NotificationNativeState.oldrequestToCloseKeys)
         .pipe(
            distinctUntilChanged(),
            filter((keys) => !_.isEmpty(keys))
         )
         .subscribe((keys) => {
            this.store.dispatch(new NativeNotificationRemoveRequestToClose(keys));
         });

      this.store
         .select(NotificationNativeState.oldOpenNotificationKeys)
         .pipe(
            distinctUntilChanged(),
            filter((keys) => !_.isEmpty(keys))
         )
         .subscribe((keys) => {
            this.store.dispatch(new NativeNotificationCloseByKey(keys));
         });
   }

   @Selector([NotificationNativeState.highestFiredKeys])
   public static getHighestFiredId(keys: NotificationKey[]) {
      return (tag: string) => {
         return keys.find((key) => key.tag === tag)?.id ?? -1;
      };
   }

   @Selector()
   private static convertKeyToString(): (key: NotificationKey) => string {
      return (key: NotificationKey) => {
         return key.tag + '-' + key.id.toString();
      };
   }

   @Selector([NotificationNativeState.convertKeyToString])
   private static convertKeysToStringArray(convertKeyToString: (key: NotificationKey) => string): (key: NotificationKey[]) => string[] {
      return (keys: NotificationKey[]) => {
         return keys.map((key) => convertKeyToString(key));
      };
   }

   @Selector([NotificationNativeState.convertKeyToString])
   private static isKeyIncluded(convertIdToString: (key: NotificationKey) => string): (keys: NotificationKey[], key: NotificationKey) => boolean {
      return (keys: NotificationKey[], key: NotificationKey) => {
         const tags = keys.map((k) => convertIdToString(k));
         const tag = convertIdToString(key);
         return tags.includes(tag);
      };
   }

   @Selector([NotificationNativeState.convertKeyToString])
   private static getDifferentKeys(
      convertKeyToString: (key: NotificationKey) => string
   ): (keys: NotificationKey[], key: NotificationKey[]) => NotificationKey[] {
      return (keys: NotificationKey[], keysToCompare: NotificationKey[]) => {
         const notificationKeys = keys.map((key) => {
            return { tagWithId: convertKeyToString(key), NotificationKey: key };
         });
         const notificationKeysToCompare = keysToCompare.map((key) => {
            return { tagWithId: convertKeyToString(key), NotificationKey: key };
         });
         const match = _.differenceBy(
            notificationKeys.map((key) => key.tagWithId),
            notificationKeysToCompare.map((key) => key.tagWithId)
         );
         return notificationKeys.filter((key) => match.includes(key.tagWithId)).map((key) => key.NotificationKey);
      };
   }

   @Selector([NotificationNativeState.convertKeyToString])
   private static getIntersectingKeys(
      convertKeyToString: (key: NotificationKey) => string
   ): (keys: NotificationKey[], key: NotificationKey[]) => NotificationKey[] {
      return (ids: NotificationKey[], keysToCompare: NotificationKey[]) => {
         const notificationKeys = ids.map((key) => {
            return { tagWithId: convertKeyToString(key), NotificationKey: key };
         });
         const notificationKeysToCompare = keysToCompare.map((key) => {
            return { tagWithId: convertKeyToString(key), NotificationKey: key };
         });
         const match = _.intersection(
            notificationKeys.map((key) => key.tagWithId),
            notificationKeysToCompare.map((key) => key.tagWithId)
         );
         return notificationKeys.filter((key) => match.includes(key.tagWithId)).map((key) => key.NotificationKey);
      };
   }

   @Selector()
   private static getsKeysWithSmallerIds(): (upToDateKeys: NotificationKey[], keysToCompare: NotificationKey[]) => NotificationKey[] {
      return (upToDateKeys: NotificationKey[], keysToCompare: NotificationKey[]) => {
         let result: NotificationKey[] = [];
         upToDateKeys.forEach((upToDateKey) => {
            const currentValues = keysToCompare.filter((key) => key.tag === upToDateKey.tag && key.id < upToDateKey.id);
            result = _.concat(result, currentValues);
         });
         return result;
      };
   }

   @Selector()
   private static convertStringToNotificationKey(): (key: string | NotificationKey) => NotificationKey {
      return (key: string | NotificationKey) => {
         const DefaultId = -1;
         const result = typeof key === 'string' ? { tag: key, id: DefaultId } : key;
         if (_.isNil(result?.id)) {
            result.id = DefaultId;
         }
         return result;
      };
   }

   @Selector([NotificationNativeState.convertStringToNotificationKey])
   private static convertNotificationIdToArray(
      convertStringToNotificationKeys: (key: string | NotificationKey) => NotificationKey
   ): (keys: NotificationKey | NotificationKey[]) => NotificationKey[] {
      return (keys: NotificationKey | NotificationKey[]) => {
         return !Array.isArray(keys) ? [convertStringToNotificationKeys(keys)] : keys.map((key) => convertStringToNotificationKeys(key));
      };
   }

   public static validateKey(key: string | NotificationKey) {
      return createSelector([NotificationNativeState.convertStringToNotificationKey], (validateKey: (key: string | NotificationKey) => NotificationKey) => {
         return validateKey(key);
      });
   }

   public static validateKeyArray(keys: NotificationKey | NotificationKey[]) {
      return createSelector(
         [NotificationNativeState.convertNotificationIdToArray],
         (validateIdArray: (idArray: NotificationKey | NotificationKey[]) => NotificationKey[]) => {
            return validateIdArray(keys);
         }
      );
   }

   public static isNotificationKeyIncluded(keys: NotificationKey[], key: NotificationKey) {
      return createSelector([NotificationNativeState.isKeyIncluded], (isKeyIncluded: (keys: NotificationKey[], key: NotificationKey) => boolean) => {
         return isKeyIncluded(keys, key);
      });
   }

   public static getDifferentNotificationKeys(keys: NotificationKey[], keysToCompare: NotificationKey[]) {
      return createSelector(
         [NotificationNativeState.getDifferentKeys],
         (getDifferentKeys: (keys: NotificationKey[], keysToCompare: NotificationKey[]) => NotificationKey[]) => {
            return getDifferentKeys(keys, keysToCompare);
         }
      );
   }

   public static getNotificationKeysWithSmallerIds(keys: NotificationKey[], keysToCompare: NotificationKey[]) {
      return createSelector(
         [NotificationNativeState.getsKeysWithSmallerIds],
         (getsKeysWithSmallerIds: (keys: NotificationKey[], keysToCompare: NotificationKey[]) => NotificationKey[]) => {
            return getsKeysWithSmallerIds(keys, keysToCompare);
         }
      );
   }

   public static getHighestFiredIdFromTag(tag: string) {
      return createSelector([NotificationNativeState.getHighestFiredId], (getHighestFiredId: (tag: string) => number) => {
         return getHighestFiredId(tag);
      });
   }

   @Selector([NotificationNativeState])
   public static highestFiredKeys(state: NotificationNativeStateModel): NotificationKey[] {
      return state?.highestFiredKeys ?? [];
   }

   @Selector([NotificationNativeState])
   public static notificationTemplate(state: NotificationNativeStateModel): NotificationTemplate[] {
      return state?.notificationTemplate ?? [];
   }

   @Selector([NotificationNativeState])
   public static openNotifications(state: NotificationNativeStateModel): NativeNotification[] {
      return state?.openNotifications ?? [];
   }

   @Selector([NotificationNativeState])
   public static closedNotifications(state: NotificationNativeStateModel): NativeNotification[] {
      return state?.closedNotifications ?? [];
   }

   @Selector([NotificationNativeState])
   public static requestToOpenKeys(state: NotificationNativeStateModel): NotificationKey[] {
      return state?.requestedToOpen ?? [];
   }

   @Selector([NotificationNativeState])
   public static requestToCloseKeys(state: NotificationNativeStateModel): NotificationKey[] {
      return state?.requestedToClose ?? [];
   }

   @Selector([NotificationNativeState.notificationTemplate])
   public static notificationTemplateKeys(notificationTemplate: NotificationTemplate[]): NotificationKey[] {
      return notificationTemplate.map((notification) => notification?.key) ?? [];
   }

   @Selector([NotificationNativeState.highestFiredKeys, NotificationNativeState.notificationTemplateKeys, NotificationNativeState.getDifferentKeys])
   public static oldTemplateKeys(
      highestFiredKeys: NotificationKey[],
      notificationTemplateKeys: NotificationKey[],
      getDifferentKeys: (keys: NotificationKey[], key: NotificationKey[]) => NotificationKey[]
   ): NotificationKey[] {
      return getDifferentKeys(notificationTemplateKeys, highestFiredKeys);
   }

   @Selector([NotificationNativeState.highestFiredKeys, NotificationNativeState.requestToOpenKeys, NotificationNativeState.getDifferentKeys])
   public static oldrequestToOpenKeys(
      highestFiredKeys: NotificationKey[],
      requestToOpenKeys: NotificationKey[],
      getDifferentKeys: (keys: NotificationKey[], key: NotificationKey[]) => NotificationKey[]
   ): NotificationKey[] {
      return getDifferentKeys(requestToOpenKeys, highestFiredKeys);
   }

   @Selector([NotificationNativeState.highestFiredKeys, NotificationNativeState.requestToCloseKeys, NotificationNativeState.getDifferentKeys])
   public static oldrequestToCloseKeys(
      highestFiredKeys: NotificationKey[],
      requestToCloseKeys: NotificationKey[],
      getDifferentKeys: (keys: NotificationKey[], key: NotificationKey[]) => NotificationKey[]
   ): NotificationKey[] {
      return getDifferentKeys(requestToCloseKeys, highestFiredKeys);
   }

   @Selector([NotificationNativeState.highestFiredKeys, NotificationNativeState.openNotificationKeys, NotificationNativeState.getDifferentKeys])
   public static oldOpenNotificationKeys(
      highestFiredKeys: NotificationKey[],
      openNotificationKeys: NotificationKey[],
      getDifferentKeys: (keys: NotificationKey[], key: NotificationKey[]) => NotificationKey[]
   ): NotificationKey[] {
      return getDifferentKeys(openNotificationKeys, highestFiredKeys);
   }

   @Selector([NotificationNativeState.openNotifications])
   public static openNotificationKeys(openNotifications: NativeNotification[]): NotificationKey[] {
      return openNotifications.map((notification) => notification?.key) ?? [];
   }

   @Selector([NotificationNativeState.closedNotifications])
   public static closedNotificationKeys(closedNotifications: NativeNotification[]): NotificationKey[] {
      return closedNotifications.map((notification) => notification?.key) ?? [];
   }

   @Selector([NotificationNativeState.openNotificationKeys, NotificationNativeState.requestToOpenKeys, NotificationNativeState.getDifferentKeys])
   private static waitingToOpenKeys(
      openNotificationKeys: NotificationKey[],
      requestToOpenKeys: NotificationKey[],
      getDifferentKeys: (keys: NotificationKey[], keysToCompare: NotificationKey[]) => NotificationKey[]
   ): NotificationKey[] {
      return getDifferentKeys(requestToOpenKeys, openNotificationKeys);
   }

   @Selector([NotificationNativeState.openNotificationKeys, NotificationNativeState.requestToCloseKeys, NotificationNativeState.getDifferentKeys])
   private static waitingToCloseKeys(
      openNotificationKeys: NotificationKey[],
      requestToCloseKeys: NotificationKey[],
      getDifferentKeys: (keys: NotificationKey[], keysToCompare: NotificationKey[]) => NotificationKey[]
   ): NotificationKey[] {
      return getDifferentKeys(requestToCloseKeys, openNotificationKeys);
   }

   @Selector([
      NotificationNativeState.openNotificationKeys,
      NotificationNativeState.requestToCloseKeys,
      NotificationNativeState.waitingToOpenKeys,
      NotificationNativeState.getIntersectingKeys,
      NotificationNativeState.getDifferentKeys,
   ])
   public static closableKeys(
      openNotificationKeys: NotificationKey[],
      requestToCloseKeys: NotificationKey[],
      waitingToOpenKeys: NotificationKey[],
      getIntersectingKeys: (keys: NotificationKey[], keysToCompare: NotificationKey[]) => NotificationKey[],
      getDifferentKeys: (keys: NotificationKey[], keysToCompare: NotificationKey[]) => NotificationKey[]
   ): NotificationKey[] {
      return getDifferentKeys(getIntersectingKeys(requestToCloseKeys, openNotificationKeys), waitingToOpenKeys);
   }

   @Selector([NotificationNativeState.openNotificationKeys, NotificationNativeState.requestToOpenKeys, NotificationNativeState.notificationTemplateKeys])
   public static keysInCreation(
      openNotificationKeys: NotificationKey[],
      requestToOpenKeys: NotificationKey[],
      notificationTemplateKeys: NotificationKey[]
   ): NotificationKey[] {
      return _.concat(openNotificationKeys, notificationTemplateKeys, requestToOpenKeys);
   }

   @Selector([NotificationNativeState.requestToCloseKeys, NotificationNativeState.keysInCreation, NotificationNativeState.getDifferentKeys])
   public static nonClosableKeys(
      requestToCloseKeys: NotificationKey[],
      keysInCreation: NotificationKey[],
      getDifferentKeys: (keys: NotificationKey[], keysToCompare: NotificationKey[]) => NotificationKey[]
   ): NotificationKey[] {
      return getDifferentKeys(requestToCloseKeys, keysInCreation);
   }

   @Selector([
      NotificationNativeState.notificationTemplateKeys,
      NotificationNativeState.requestToCloseKeys,
      NotificationNativeState.requestToOpenKeys,
      NotificationNativeState.getDifferentKeys,
   ])
   public static createAbleNotificationKeys(
      notificationTemplateKeys: NotificationKey[],
      requestToCloseKeys: NotificationKey[],
      requestToOpenKeys: NotificationKey[],
      getDifferentKeys: (keys: NotificationKey[], keysToCompare: NotificationKey[]) => NotificationKey[]
   ) {
      return getDifferentKeys(getDifferentKeys(notificationTemplateKeys, requestToCloseKeys), requestToOpenKeys);
   }

   @Selector([NotificationNativeState.notificationTemplate, NotificationNativeState.createAbleNotificationKeys, NotificationNativeState.isKeyIncluded])
   public static createAbleNotifications(
      notificationTemplate: NotificationTemplate[],
      createAbleNotificationKeys: NotificationKey[],
      isKeyIncluded: (keys: NotificationKey[], key: NotificationKey) => boolean
   ) {
      return notificationTemplate.filter((notification) => isKeyIncluded(createAbleNotificationKeys, notification?.key));
   }

   @Selector([NotificationNativeState.notificationTemplateKeys, NotificationNativeState.createAbleNotificationKeys, NotificationNativeState.getDifferentKeys])
   public static notCreateAbleNotificationKeys(
      notificationTemplateKeys: NotificationKey[],
      createAbleNotificationKeys: NotificationKey[],
      getDifferentKeys: (keys: NotificationKey[], keysToCompare: NotificationKey[]) => NotificationKey[]
   ) {
      return getDifferentKeys(notificationTemplateKeys, createAbleNotificationKeys);
   }

   @Selector([NotificationNativeState.waitingToOpenKeys])
   public static isWaitingToOpen(waitingToOpen: NotificationKey[]): boolean {
      return !_.isEmpty(waitingToOpen);
   }

   @Selector([NotificationNativeState.waitingToCloseKeys])
   public static isWaitingToClose(waitingToClose: NotificationKey[]): boolean {
      return !_.isEmpty(waitingToClose);
   }

   @Action(NativeNotificationAddTemplate)
   public nativeNotificationAddTemplate(ctx: StateContext<NotificationNativeStateModel>, action: NativeNotificationAddTemplate) {
      const notificationKey = this.store.selectSnapshot(NotificationNativeState.validateKey(action.key));
      const valuesToUpdate = _.cloneDeep(ctx.getState().notificationTemplate);
      this.store.selectSnapshot(NotificationNativeState.validateKey(action.key));
      const alreadyIncluded = this.store.selectSnapshot(
         NotificationNativeState.isNotificationKeyIncluded(
            valuesToUpdate.map((value) => value?.key),
            notificationKey
         )
      );
      if (alreadyIncluded) {
         return;
      }
      valuesToUpdate.push({ key: notificationKey, notificationContent: action.notificationContent });
      ctx.patchState({
         notificationTemplate: valuesToUpdate,
      });
      ctx.dispatch(new NativeNotificationSetHighestFiredNativeKeys(notificationKey));
   }

   @Action(NativeNotificationRemoveTemplate)
   public nativeNotificationRemoveTemplate(ctx: StateContext<NotificationNativeStateModel>, action: NativeNotificationRemoveTemplate) {
      const keysToRemove = this.store.selectSnapshot(NotificationNativeState.validateKeyArray(action?.keys));
      if (_.isEmpty(keysToRemove)) {
         return;
      }
      const valuesToUpdate = _.cloneDeep(ctx.getState().notificationTemplate);
      const valuestoKeep = valuesToUpdate.filter(
         (notification) => !this.store.selectSnapshot(NotificationNativeState.isNotificationKeyIncluded(keysToRemove, notification?.key))
      );
      ctx.patchState({
         notificationTemplate: valuestoKeep,
      });
   }

   @Action(NativeNotificationSetOpenNotifications)
   public nativeNotificationSetOpenNotifications(ctx: StateContext<NotificationNativeStateModel>, action: NativeNotificationSetOpenNotifications) {
      const newNotifications = action.notifications.map((notification) => {
         const notificationKey = this.store.selectSnapshot(
            NotificationNativeState.validateKey({
               tag: notification?.tag,
               id: notification?.data,
            })
         );
         return { key: notificationKey, notification: notification };
      });
      const newNotificationKeys = newNotifications.map((notification) => notification.key);
      const diplayedNotifications = _.cloneDeep(ctx.getState().openNotifications);
      const missingNotifications = diplayedNotifications.filter(
         (notification) => !this.store.selectSnapshot(NotificationNativeState.isNotificationKeyIncluded(newNotificationKeys, notification?.key))
      );

      const missingNotificationKeys = missingNotifications.map((notification) => notification?.key);
      ctx.patchState({
         openNotifications: _.concat(newNotifications, missingNotifications),
      });
      if (!_.isEmpty(newNotificationKeys)) {
         ctx.dispatch(new NativeNotificationRemoveRequestToOpen(newNotificationKeys));
      }
      if (!_.isEmpty(missingNotificationKeys)) {
         ctx.dispatch(new NativeNotificationCloseByKey(missingNotificationKeys));
      }
   }

   @Action(NativeNotificationCloseByKey)
   public nativeNotificationCloseByKey(ctx: StateContext<NotificationNativeStateModel>, action: NativeNotificationCloseByKey) {
      const diplayedNotifications = _.cloneDeep(ctx.getState().openNotifications);
      const keysToRemove = this.store.selectSnapshot(NotificationNativeState.validateKeyArray(action?.keys));
      ctx.patchState({
         openNotifications: diplayedNotifications.filter(
            (notification) => !this.store.selectSnapshot(NotificationNativeState.isNotificationKeyIncluded(keysToRemove, notification?.key))
         ),
         closedNotifications: diplayedNotifications.filter((notification) =>
            this.store.selectSnapshot(NotificationNativeState.isNotificationKeyIncluded(keysToRemove, notification?.key))
         ),
      });
   }

   @Action(NativeNotificationFinishClose)
   public nativeNotificationFinishClose(ctx: StateContext<NotificationNativeStateModel>, action: NativeNotificationFinishClose) {
      const notifications = _.cloneDeep(ctx.getState().closedNotifications);
      const keysToRemove = this.store.selectSnapshot(NotificationNativeState.validateKeyArray(action?.keys));
      ctx.patchState({
         closedNotifications: notifications.filter(
            (notification) => !this.store.selectSnapshot(NotificationNativeState.isNotificationKeyIncluded(keysToRemove, notification?.key))
         ),
      });
      ctx.dispatch(new NativeNotificationRemoveRequestToClose(action.keys));
   }

   @Action(NativeNotificationAddRequestToOpen)
   public nativeNotificationAddRequestToOpen(ctx: StateContext<NotificationNativeStateModel>, action: NativeNotificationAddRequestToOpen) {
      const notificationKey = this.store.selectSnapshot(NotificationNativeState.validateKey(action?.key));
      const requestedToOpenKeys = _.cloneDeep(ctx.getState().requestedToOpen);
      const alreadyIncluded = this.store.selectSnapshot(NotificationNativeState.isNotificationKeyIncluded(requestedToOpenKeys, notificationKey));
      if (alreadyIncluded) {
         return;
      }
      requestedToOpenKeys.push(notificationKey);
      ctx.patchState({
         requestedToOpen: requestedToOpenKeys,
      });
   }

   @Action(NativeNotificationAddRequestToClose)
   public nativeNotificationAddRequestToClose(ctx: StateContext<NotificationNativeStateModel>, action: NativeNotificationAddRequestToClose) {
      const notificationKey = this.store.selectSnapshot(NotificationNativeState.validateKey(action?.key));
      const requestedToCloseKeys = _.cloneDeep(ctx.getState().requestedToClose);
      const alreadyIncluded = this.store.selectSnapshot(NotificationNativeState.isNotificationKeyIncluded(requestedToCloseKeys, notificationKey));
      if (alreadyIncluded) {
         return;
      }
      requestedToCloseKeys.push(notificationKey);
      ctx.patchState({
         requestedToClose: requestedToCloseKeys,
      });
   }

   @Action(NativeNotificationRemoveRequestToOpen)
   public nativeNotificationRemoveRequestToOpen(ctx: StateContext<NotificationNativeStateModel>, action: NativeNotificationRemoveRequestToOpen) {
      const keysToRemove = this.store.selectSnapshot(NotificationNativeState.validateKeyArray(action?.keys));
      if (_.isEmpty(keysToRemove)) {
         return;
      }
      const requestedToOpenKeys = _.cloneDeep(ctx.getState().requestedToOpen);
      ctx.patchState({
         requestedToOpen: this.store.selectSnapshot(NotificationNativeState.getDifferentNotificationKeys(requestedToOpenKeys, keysToRemove)),
      });
   }

   @Action(NativeNotificationRemoveRequestToClose)
   public nativeNotificationRemoveRequestToClose(ctx: StateContext<NotificationNativeStateModel>, action: NativeNotificationRemoveRequestToClose) {
      const keysToRemove = this.store.selectSnapshot(NotificationNativeState.validateKeyArray(action?.keys));
      if (_.isEmpty(keysToRemove)) {
         return;
      }
      const requestedToCloseKeys = _.cloneDeep(ctx.getState().requestedToClose);
      ctx.patchState({
         requestedToClose: this.store.selectSnapshot(NotificationNativeState.getDifferentNotificationKeys(requestedToCloseKeys, keysToRemove)),
      });
   }

   @Action(NativeNotificationSetHighestFiredNativeKeys)
   public nativeNotificationSetHighestFiredNativeKeys(ctx: StateContext<NotificationNativeStateModel>, action: NativeNotificationSetHighestFiredNativeKeys) {
      const requestedKeys = this.store.selectSnapshot(NotificationNativeState.validateKeyArray(action?.keys));
      if (_.isEmpty(requestedKeys)) {
         return;
      }
      const currentHighestFiredKeys = _.cloneDeep(ctx.getState().highestFiredKeys);
      const higherKeys = requestedKeys.filter((key) => key.id > this.store.selectSnapshot(NotificationNativeState.getHighestFiredIdFromTag(key.tag)));
      if (_.isEmpty(higherKeys)) {
         return;
      }
      const oldValues = currentHighestFiredKeys.filter((currentKey) => !higherKeys.map((higherKey) => higherKey.tag).includes(currentKey.tag));
      ctx.patchState({
         highestFiredKeys: _.concat(oldValues, higherKeys),
      });
   }
}
